import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import * as turf from '@turf/turf';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useUtmMapSettings } from '@/hooks/utm';

interface UtmGeometryPreviewProps {
  geometry: any;
  style?: {
    fillColor?: string;
    outlineColor?: string;
    fillOpacity?: number;
    outlineWidth?: number;
  };
  height?: string;
}

const UtmGeometryPreview = ({
  geometry,
  style = {},
  height = '300px',
}: UtmGeometryPreviewProps) => {
  const { data: mapSettings } = useUtmMapSettings();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const fillColor = style.fillColor || '#3b82f6';
  const outlineColor = style.outlineColor || '#1e40af';
  const fillOpacity = style.fillOpacity ?? 0.3;
  const outlineWidth = style.outlineWidth ?? 2;

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const mapInstance = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
      center: mapSettings
        ? [mapSettings.longitude, mapSettings.latitude]
        : [0, 0],
      zoom: mapSettings?.zoom ?? 1,
    });

    map.current = mapInstance;

    mapInstance.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      mapInstance.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, [mapSettings]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !mapSettings || geometry) return;
    map.current.jumpTo({
      center: [mapSettings.longitude, mapSettings.latitude],
      zoom: mapSettings.zoom,
    });
  }, [mapLoaded, mapSettings, geometry]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !geometry) return;

    const sourceId = 'utm-preview-source';
    const fillLayerId = 'utm-preview-fill';
    const lineLayerId = 'utm-preview-line';

    if (map.current.getLayer(lineLayerId)) {
      map.current.removeLayer(lineLayerId);
    }
    if (map.current.getLayer(fillLayerId)) {
      map.current.removeLayer(fillLayerId);
    }
    if (map.current.getSource(sourceId)) {
      map.current.removeSource(sourceId);
    }

    const geoJsonData = {
      type: 'Feature' as const,
      properties: {},
      geometry,
    };

    map.current.addSource(sourceId, {
      type: 'geojson',
      data: geoJsonData,
    });

    map.current.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': fillColor,
        'fill-opacity': fillOpacity,
      },
    });

    map.current.addLayer({
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': outlineColor,
        'line-width': outlineWidth,
      },
    });

    try {
      const bbox = turf.bbox(geometry);
      map.current.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding: 40, duration: 0 }
      );
    } catch (error) {
      console.error('Failed to fit bounds:', error);
    }
  }, [geometry, mapLoaded, fillColor, outlineColor, fillOpacity, outlineWidth]);

  if (!geometry) {
    return (
      <div
        className="bg-muted border border-border rounded-lg flex items-center justify-center"
        style={{ height }}
      >
        <p className="text-sm text-muted-foreground">No geometry to preview</p>
      </div>
    );
  }

  return (
    <div
      ref={mapContainer}
      className="bg-muted border border-border rounded-lg overflow-hidden"
      style={{ height }}
    />
  );
};

export default UtmGeometryPreview;
